import React, { useState, useRef, useEffect, useId } from 'react';
import apiService from '../services/ApiService';

export type VehicleSuggestField = 'chassis' | 'engine' | 'transmission';

interface VehicleFieldComboboxProps {
  field: VehicleSuggestField;
  value: string;
  onChange: (value: string) => void;
  id?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  /** When false, free-text still works but the “Use new value” row is hidden (filters). */
  allowCreate?: boolean;
}

/**
 * Free-text + suggestions for chassis / engine / transmission.
 * Falls back to a normal text input if the suggestions endpoint fails.
 */
const VehicleFieldCombobox: React.FC<VehicleFieldComboboxProps> = ({
  field,
  value,
  onChange,
  id,
  className = '',
  placeholder,
  disabled = false,
  allowCreate = true,
}) => {
  const listboxId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [suggestionsFailed, setSuggestionsFailed] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  const fetchSuggestions = (q: string) => {
    if (suggestionsFailed || disabled) return;

    const reqId = ++requestIdRef.current;
    setLoading(true);

    apiService
      .getVehicleFieldSuggestions(field, q, 10)
      .then((list) => {
        if (reqId !== requestIdRef.current) return;
        setSuggestions(list);
        setLoading(false);
      })
      .catch(() => {
        if (reqId !== requestIdRef.current) return;
        setSuggestionsFailed(true);
        setSuggestions([]);
        setLoading(false);
        setIsOpen(false);
      });
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const scheduleFetch = (q: string) => {
    if (suggestionsFailed) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(q), 220);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const next = e.target.value;
    onChange(next);
    if (!suggestionsFailed) {
      setIsOpen(true);
      setHighlight(-1);
      scheduleFetch(next);
    }
  };

  const handleSelect = (item: string) => {
    if (disabled) return;
    onChange(item);
    setIsOpen(false);
    setHighlight(-1);
    inputRef.current?.blur();
  };

  const handleFocus = () => {
    if (disabled || suggestionsFailed) return;
    setIsOpen(true);
    scheduleFetch(value);
  };

  const handleBlur = (e: React.FocusEvent) => {
    setTimeout(() => {
      if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
        setIsOpen(false);
        setHighlight(-1);
      }
    }, 150);
  };

  const trimmed = value.trim();
  const exactMatch = suggestions.some((s) => s.toLowerCase() === trimmed.toLowerCase());
  const showUseNew = allowCreate && Boolean(trimmed) && !exactMatch;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (suggestionsFailed || !isOpen) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
      return;
    }

    const options = [
      ...suggestions,
      ...(showUseNew ? [`__new__:${trimmed}`] : []),
    ];

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, -1));
    } else if (e.key === 'Enter' && highlight >= 0 && options[highlight]) {
      e.preventDefault();
      const opt = options[highlight];
      if (opt.startsWith('__new__:')) {
        handleSelect(trimmed);
      } else {
        handleSelect(opt);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlight(-1);
      inputRef.current?.blur();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const inputClass =
    `input disabled:opacity-50 ${className}`;

  return (
    <div className="relative min-w-0" ref={dropdownRef}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={inputClass}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen && !suggestionsFailed}
        aria-controls={listboxId}
        aria-autocomplete="list"
      />

      {isOpen && !disabled && !suggestionsFailed && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-50 w-full mt-1 surface-raised rounded-lg shadow-elevated max-h-60 overflow-y-auto overflow-x-hidden"
        >
          {loading && (
            <div className="px-3 py-2 text-gray-400 text-sm">Loading suggestions…</div>
          )}

          {!loading && suggestions.length === 0 && !showUseNew && (
            <div className="px-3 py-2 text-gray-400 text-sm">
              {trimmed ? 'No matches. Keep typing to use a new value.' : 'Start typing for suggestions'}
            </div>
          )}

          {suggestions.map((item, index) => (
            <button
              key={item}
              type="button"
              role="option"
              aria-selected={highlight === index}
              onClick={() => handleSelect(item)}
              className={`w-full text-left px-3 py-2 text-white focus:outline-none break-words ${
                highlight === index ? 'bg-[#1a1a1a]' : 'hover:bg-white/[0.06]'
              }`}
            >
              {item}
            </button>
          ))}

          {showUseNew && (
            <>
              {suggestions.length > 0 && <div className="border-t border-white/[0.10] my-1" />}
              <button
                type="button"
                role="option"
                aria-selected={highlight === suggestions.length}
                onClick={() => handleSelect(trimmed)}
                className={`w-full text-left px-3 py-2 text-green-400 focus:outline-none break-words ${
                  highlight === suggestions.length ? 'bg-[#1a1a1a]' : 'hover:bg-white/[0.06]'
                }`}
              >
                Use new value “{trimmed}”
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default VehicleFieldCombobox;
